import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateEventAutomationCoverage } from './useEventAutomationCoverage';

const MAX_CONTACTS_PER_EVENT_AUTOMATION_START = 25;

interface MutationVariables {
	surveyId: string;
	contactIds: string[];
	impersonationContext?: Api.IImpersonationContext;
}

export interface EventAutomationStartOutcome extends Api.IEventAutomationStartResult {
	/** Set when a later batch failed after earlier ones started; the rest were not attempted. */
	stoppedEarlyError?: Api.IOperationResultNoValue;
	notAttemptedContactIds: string[];
}

/** Carries what each batch reported, so already-enrolled contacts are still shown when nothing started. */
export type EventAutomationStartError = Api.IOperationResultNoValue & { outcome?: EventAutomationStartOutcome };

const chunk = (ids: string[]) => {
	const chunks: string[][] = [];
	for (let i = 0; i < ids.length; i += MAX_CONTACTS_PER_EVENT_AUTOMATION_START) {
		chunks.push(ids.slice(i, i + MAX_CONTACTS_PER_EVENT_AUTOMATION_START));
	}
	return chunks;
};

export function useStartEventAutomationMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (outcome: EventAutomationStartOutcome) => void;
	onError?: (error: EventAutomationStartError) => void;
} = {}) {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: async ({ surveyId, contactIds, impersonationContext }: MutationVariables) => {
			const outcome: EventAutomationStartOutcome = {
				startedContactIds: [],
				skippedContactIds: [],
				failedContactIds: [],
				notAttemptedContactIds: [],
			};
			const merge = (result?: Partial<Api.IEventAutomationStartResult>) => {
				outcome.startedContactIds.push(...(result?.startedContactIds ?? []));
				outcome.skippedContactIds.push(...(result?.skippedContactIds ?? []));
				outcome.failedContactIds.push(...(result?.failedContactIds ?? []));
			};

			const batches = chunk([...new Set(contactIds)]);
			for (const [index, batch] of batches.entries()) {
				// Caught per batch: the server caps a call at 25 contacts, and batches that already started must still be reported.
				try {
					merge(
						await userSession.webServiceHelper.callAsync<Api.IEventAutomationStartResult>(
							Api.ImpersonationBroker.composeApiUrl({
								impersonationContext,
								urlPath: `survey/eventRegistration/${encodeURIComponent(surveyId)}/automation/start`,
							}),
							'POST',
							{ contactIds: batch }
						)
					);
				} catch (error) {
					const apiError = Api.asApiError(error) as Api.IOperationResult<Api.IEventAutomationStartResult>;
					if (apiError.value) {
						merge(apiError.value);
					} else {
						outcome.failedContactIds.push(...batch);
					}
					outcome.notAttemptedContactIds = batches.slice(index + 1).flat();
					if (outcome.startedContactIds.length === 0) {
						const startError: EventAutomationStartError = { ...apiError, outcome };
						throw startError;
					}
					outcome.stoppedEarlyError = apiError;
					break;
				}
			}
			return outcome;
		},
		onSuccess,
		onError,
		onSettled: () => {
			invalidateEventAutomationCoverage();
		},
	});
}
